// ============================================================================
// Client-side notification scheduler using Notification API + Service Worker
// ============================================================================

import { supabase } from '@/lib/supabase';

export interface NotificationSettings {
  enabled: boolean;
  days: boolean[]; // [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
  time: string; // "HH:MM" format
}

const STORAGE_KEY = 'notificationSettings';
const TIMER_KEY = '_notificationTimer';

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: false,
  days: [false, true, true, true, true, true, false], // Mon-Fri
  time: '21:00',
};

export function getNotificationSettings(): NotificationSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return { ...DEFAULT_SETTINGS };
}

export async function saveNotificationSettings(settings: NotificationSettings): Promise<void> {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));

  // サーバー同期: Push subscription + 設定を DB に保存
  try {
    const { subscribePush, unsubscribePush, saveNotificationSettingsToServer } = await import('@/lib/pushNotifications');
    if (settings.enabled) {
      await subscribePush();
    } else {
      await unsubscribePush();
    }
    await saveNotificationSettingsToServer(settings);
  } catch (err) {
    console.warn('[Notifications] Server sync failed, local-only:', err);
  }

  // ローカルスケジューラもフォールバックとして維持
  scheduleNotification();
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

async function showNotification(): Promise<void> {
  let title = '今日の日記を書こう！';
  let body = '今日の出来事を記録しましょう';

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const { count, error } = await supabase
        .from('entries')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('date', today);

      if (!error && count === 0) {
        title = '30秒だけ、今の気分を吐き出して';
        body = '今日はどんな一日だった？声で30秒、吐き出すだけでOK';
      } else if (!error && count !== null && count > 0) {
        // 既にエントリーがある場合は通知をスキップ
        return;
      }
    }
  } catch {
    // フォールバック: デフォルトメッセージで通知
  }

  const options: NotificationOptions = {
    body,
    icon: '/pwa-192x192.png',
    tag: 'daily-reminder',
  };

  // Prefer Service Worker notification (works in background)
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then(reg => {
      reg.showNotification(title, options);
    });
  } else if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, options);
  }
}

export function scheduleNotification(): void {
  // Clear any existing timer
  const existingTimer = (window as any)[TIMER_KEY];
  if (existingTimer) {
    clearTimeout(existingTimer);
    (window as any)[TIMER_KEY] = null;
  }

  const settings = getNotificationSettings();
  if (!settings.enabled) return;
  if (Notification.permission !== 'granted') return;

  const now = new Date();
  const [hours, minutes] = settings.time.split(':').map(Number);

  // Find next scheduled time
  let target = new Date(now);
  target.setHours(hours, minutes, 0, 0);

  // If today's time has passed, start checking from tomorrow
  if (target <= now) {
    target.setDate(target.getDate() + 1);
  }

  // Find the next enabled day
  for (let i = 0; i < 7; i++) {
    const dayOfWeek = target.getDay();
    if (settings.days[dayOfWeek]) break;
    target.setDate(target.getDate() + 1);
  }

  const delay = target.getTime() - now.getTime();

  // Only schedule if within 24 hours (re-schedule on next app load otherwise)
  if (delay > 24 * 60 * 60 * 1000) return;

  (window as any)[TIMER_KEY] = setTimeout(async () => {
    try {
      await showNotification();
    } catch {
      // 通知失敗時もスケジュールを継続
    }
    scheduleNotification();
  }, delay);
}
